import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CloudArrowUp, Trash, ArrowClockwise, Warning, CheckCircle, X,
} from "@phosphor-icons/react";
import { useLang } from "@/i18n";
import {
  api, subscribeQueue, drainQueue, listQueue, removeItem, retryItem, clearFailed,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

const METHOD_COLOR = {
  POST: "bg-emerald-100 text-emerald-800 border-emerald-200",
  PUT: "bg-blue-100 text-blue-800 border-blue-200",
  PATCH: "bg-indigo-100 text-indigo-800 border-indigo-200",
  DELETE: "bg-red-100 text-red-800 border-red-200",
};

function fmt(iso) {
  try { return new Date(iso).toLocaleString(); } catch (_e) { return iso; }
}

/**
 * Inspects the offline sync queue: shows pending and failed mutations with the
 * ability to retry, delete or bulk clear failed entries.
 */
export default function SyncQueueDrawer({ open, onOpenChange }) {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const rows = await listQueue();
    // Sort: pending first (by creation asc), then failed
    rows.sort((a, b) => (a.failed === b.failed ? new Date(a.created_at) - new Date(b.created_at) : a.failed ? 1 : -1));
    setItems(rows);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    refresh();
    const unsub = subscribeQueue(() => refresh());
    const onSynced = () => refresh();
    window.addEventListener("offline-queue-synced", onSynced);
    return () => { unsub(); window.removeEventListener("offline-queue-synced", onSynced); };
  }, [open, refresh]);

  const handleRetryAll = async () => {
    if (!navigator.onLine) { toast.warning(t("wentOffline")); return; }
    setBusy(true);
    try {
      // Reset failed items so drainQueue picks them up
      for (const it of items) if (it.failed) await retryItem(it.id);
      const result = await drainQueue(api);
      if (result.synced > 0) toast.success(`${result.synced} ${t("changesSynced")}`);
      if (result.failed > 0) toast.error(`${result.failed} ${t("changesFailed")}`);
      if (result.synced === 0 && result.failed === 0) toast.info(t("nothingToSync"));
    } finally { setBusy(false); refresh(); }
  };

  const handleRetryOne = async (id) => {
    if (!navigator.onLine) { toast.warning(t("wentOffline")); return; }
    await retryItem(id);
    await drainQueue(api);
    refresh();
  };

  const handleDeleteOne = async (id) => {
    if (!window.confirm(t("confirmDeleteChange"))) return;
    await removeItem(id);
    refresh();
  };

  const handleClearFailed = async () => {
    if (!window.confirm(t("confirmClearFailed"))) return;
    await clearFailed();
    refresh();
  };

  const pending = items.filter((i) => !i.failed);
  const failed = items.filter((i) => i.failed);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-white w-full sm:max-w-2xl overflow-y-auto" data-testid="sync-queue-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading flex items-center gap-2">
            <CloudArrowUp size={22} weight="duotone" /> {t("syncQueueTitle")}
          </SheetTitle>
          <SheetDescription>{t("syncQueueDesc")}</SheetDescription>
        </SheetHeader>

        <div className="flex flex-wrap items-center gap-2 mb-2 mt-4">
          <span className="text-xs text-slate-500">
            {t("pending")}: <b data-testid="drawer-pending-count" className="text-blue-900">{pending.length}</b>
          </span>
          <span className="text-xs text-slate-500">
            {t("failed")}: <b data-testid="drawer-failed-count" className="text-red-800">{failed.length}</b>
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm" variant="outline" onClick={handleRetryAll}
              disabled={busy || items.length === 0}
              data-testid="drawer-retry-all"
              className="gap-1"
            >
              <ArrowClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} /> {t("retryAll")}
            </Button>
            {failed.length > 0 && (
              <Button
                size="sm" variant="outline" onClick={handleClearFailed}
                data-testid="drawer-clear-failed"
                className="gap-1 text-red-700 border-red-200 hover:bg-red-50"
              >
                <Trash size={14} weight="bold" /> {t("clearFailed")}
              </Button>
            )}
          </div>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <CheckCircle size={40} weight="duotone" className="mx-auto mb-2 text-emerald-500" />
            <p className="text-sm">{t("nothingToSync")}</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((it) => (
              <li key={it.id} data-testid={`sync-item-${it.id}`}
                  className={"border rounded-md p-2 " + (it.failed ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200")}>
                <div className="flex items-start gap-2">
                  <span className={"text-[10px] font-bold px-2 py-0.5 rounded border " + (METHOD_COLOR[it.method] || "bg-slate-100 text-slate-700 border-slate-200")}>
                    {it.method}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-mono text-slate-800 truncate" title={it.url}>{it.url}</p>
                    <p className="text-[10px] text-slate-500">
                      {fmt(it.created_at)}
                      {it.attempts > 0 && (<> · {t("attempts")}: <b>{it.attempts}</b></>)}
                      {it.last_status ? <> · HTTP {it.last_status}</> : null}
                    </p>
                    {it.failed && it.last_error && (
                      <p className="text-[11px] text-red-700 mt-1 flex items-start gap-1">
                        <Warning size={12} weight="fill" className="mt-0.5 shrink-0" />
                        <span className="break-words">{it.last_error}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {it.failed && (
                      <button
                        onClick={() => handleRetryOne(it.id)}
                        data-testid={`sync-retry-${it.id}`}
                        title={t("retry")}
                        className="text-blue-700 hover:text-blue-900 p-1"
                      >
                        <ArrowClockwise size={14} weight="bold" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteOne(it.id)}
                      data-testid={`sync-discard-${it.id}`}
                      title={t("discard")}
                      className="text-slate-400 hover:text-red-600 p-1"
                    >
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </SheetContent>
    </Sheet>
  );
}
